import { useEffect, useMemo, useState } from 'react';
import TextWinds from '../../../common/TextWinds';
import ClassAssignList from './ClassAssignList';
import {
  ClassGroupType,
  studentType,
} from '../../../../constants/types/student-type';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import {
  unassignStudentFromClassGroup,
  assignStudentToClassGroup,
} from '../../../../api/utils/lms/student/api';
import HFbutton from '../../../common/input/HFbutton';
import FormDialog from '../../../common/FormDialog';
import { ClassGroupDescriptionSection } from '../../class-group/ClassGroupDescriptionSection';

interface ClassGroupAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  classId: string;
  studentList: studentType[];
  reloadTable: () => void;
}

/**
 * LMS > 학생 관리 > 반 배정
 *
 * 선택한 학생들을 한 반에 배정하거나 배정을 해제하는 팝업.
 * '신규 학생 등록'과 동일한 FormDialog 형태를 쓴다.
 */
const ClassGroupAssignmentDialog = ({
  open,
  onClose,
  classId,
  studentList,
  reloadTable,
}: ClassGroupAssignmentDialogProps) => {
  const dispatch = useAppDispatch();

  // 학생이 1명일 경우, 해당 학생의 반 정보를 기본값으로 설정
  const defaultClassGroup: ClassGroupType = useMemo(() => {
    if (studentList && studentList.length === 1) {
      const classGroup = studentList[0].classInfos;
      if (classGroup.length > 0) {
        return classGroup[0].classGroup;
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }, [studentList]);

  const [selected, setSelected] = useState<ClassGroupType>(defaultClassGroup);

  // 팝업은 항상 마운트돼 있으므로, 열릴 때마다 대상 학생 기준으로 선택값을 맞춘다.
  useEffect(() => {
    if (open) setSelected(defaultClassGroup);
  }, [open, defaultClassGroup]);

  const assignHandler = () => {
    if (!selected) {
      dispatch(openAlert({ message: '배정할 반을 선택해주세요.' }));
      return;
    }

    dispatch(
      openConfirm({
        message: `학생 ${studentList.length}명을 반에 배정하시겠습니까?`,
        onConfirm: () => {
          assignStudentToClassGroup(classId, {
            classGroupId: selected.id,
            studentIds: studentList.map((item) => item.id),
          }).then(() => {
            reloadTable();
            onClose();
          });
        },
      }),
    );
  };

  const deleteHandler = () => {
    dispatch(
      openConfirm({
        message: `학생 ${studentList.length}명의 반 배정을 해제하시겠습니까?`,
        onConfirm: () => {
          studentList.forEach((student) => {
            student.classInfos.forEach((classInfo) => {
              unassignStudentFromClassGroup(
                classId,
                student.id,
                classInfo.classGroup?.id,
              ).then(() => {
                reloadTable();
                onClose();
              });
            });
          });
        },
      }),
    );
  };

  // studentList 가 빈 배열이면 제목을 만들 수 없으므로 렌더하지 않는다.
  if (!open || studentList.length === 0) return null;

  const title =
    studentList.length > 1
      ? `${studentList.length}명 반 배정하기`
      : `'${studentList[0].name}' 반 배정하기`;

  return (
    <FormDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <HFbutton onClick={assignHandler}>완료</HFbutton>
          <HFbutton type="Outlined" onClick={onClose}>
            취소
          </HFbutton>
        </div>
      }
    >
      <div className="flex flex-col gap-2.5">
        <section className="flex items-center justify-between gap-2">
          <TextWinds
            type="content_body3"
            color="gray7"
            className="whitespace-pre-wrap break-words"
          >
            클래스에 등록된 반 중 활성화된 반에만 학생을 배정할 수 있습니다.
          </TextWinds>
          <HFbutton
            type="orange-Outlined"
            height={34}
            onClick={deleteHandler}
            width="auto"
          >
            배정 해제
          </HFbutton>
        </section>

        <ClassAssignList
          classId={classId}
          setSelected={setSelected}
          selected={defaultClassGroup}
        />

        <ClassGroupDescriptionSection />
      </div>
    </FormDialog>
  );
};

export default ClassGroupAssignmentDialog;
